import type { MantineTheme } from '@mantine/core';
import type { CSSProperties } from 'react';

/** dhtmlx Gantt `data-gantt-theme` / ReactGantt `theme` prop values. */
export type DhtmlxGanttThemeName = 'terrace' | 'dark';

/** Light uses ReactGantt default `terrace` (green project bars). Dark uses `dark`. */
export function getDhtmlxGanttThemeName(colorScheme: 'light' | 'dark'): DhtmlxGanttThemeName {
  return colorScheme === 'dark' ? 'dark' : 'terrace';
}

/**
 * Override dhtmlx CSS variables for grid, scale, and timeline chrome (not task bar colors).
 * Task bars use `task_class` + gantt-custom.css (location / activity / completed / critical).
 * @see node_modules/@dhx/react-gantt/dist/react-gantt.css
 */
export function getDhtmlxGanttCssVariables(
  theme: MantineTheme,
  colorScheme: 'light' | 'dark'
): CSSProperties {
  const isDark = colorScheme === 'dark';
  const d = theme.colors.dark;
  const g = theme.colors.gray;

  if (!isDark) {
    return {
      /** Drives ReactGantt skin poller (getComputedStyle(--dhx-gantt-theme)). */
      '--dhx-gantt-theme': 'terrace',
      '--dhx-gantt-btn-text-transform': 'none',
      '--dhx-gantt-container-background': theme.white,
      '--dhx-gantt-container-color': d[7],
      '--dhx-gantt-base-colors-background': theme.white,
      '--dhx-gantt-base-colors-background-alt': g[0],
      '--dhx-gantt-base-colors-text-base': d[7],
      '--dhx-gantt-base-colors-text-light': g[7],
      '--dhx-gantt-base-colors-border': g[3],
      '--dhx-gantt-base-colors-border-light': g[2],
      '--dhx-gantt-base-colors-hover-color': g[0],
      '--dhx-gantt-base-colors-select': theme.colors.brand[0],
      '--dhx-gantt-base-colors-icons': g[7],
      '--dhx-gantt-grid-body-background': theme.white,
      '--dhx-gantt-grid-scale-background': g[0],
      '--dhx-gantt-grid-scale-color': d[6],
      '--dhx-gantt-timeline-scale-background': g[0],
      '--dhx-gantt-timeline-scale-color': d[6],
      '--dhx-gantt-scale-background': g[0],
      '--dhx-gantt-scale-color': d[6],
      '--dhx-gantt-task-row-background': theme.white,
      '--dhx-gantt-task-row-background--odd': theme.white,
      '--dhx-gantt-grid-row-border': `1px solid ${g[2]}`,
      '--dhx-gantt-grid-cell-border': `1px solid ${g[2]}`,
      '--dhx-gantt-offtime-background': g[1],
    };
  }

  return {
    '--dhx-gantt-theme': 'dark',
    '--dhx-gantt-btn-text-transform': 'none',
    '--dhx-gantt-container-background': d[7],
    '--dhx-gantt-container-color': g[0],
    '--dhx-gantt-base-colors-background': d[7],
    '--dhx-gantt-base-colors-background-alt': d[6],
    '--dhx-gantt-base-colors-text-base': g[0],
    '--dhx-gantt-base-colors-text-light': g[4],
    '--dhx-gantt-base-colors-border': d[4],
    '--dhx-gantt-base-colors-border-light': d[5],
    '--dhx-gantt-base-colors-hover-color': d[5],
    '--dhx-gantt-base-colors-select': theme.colors.brand[9],
    '--dhx-gantt-base-colors-icons': g[4],
    '--dhx-gantt-grid-body-background': d[7],
    '--dhx-gantt-grid-scale-background': d[6],
    '--dhx-gantt-grid-scale-color': g[2],
    '--dhx-gantt-timeline-scale-background': d[6],
    '--dhx-gantt-timeline-scale-color': g[2],
    '--dhx-gantt-scale-background': d[6],
    '--dhx-gantt-scale-color': g[2],
    '--dhx-gantt-task-row-background': d[7],
    '--dhx-gantt-task-row-background--odd': d[7],
    '--dhx-gantt-grid-row-border': `1px solid ${d[4]}`,
    '--dhx-gantt-grid-cell-border': `1px solid ${d[4]}`,
    '--dhx-gantt-offtime-background': d[8],
    '--dhx-gantt-timeline-scale-border-vertical': `1px solid ${d[4]}`,
    '--dhx-gantt-grid-scale-border-vertical': `1px solid ${d[4]}`,
    '--dhx-gantt-scale-border-horizontal': `1px solid ${d[4]}`,
  };
}
